import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/accordion'
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert'
import { FormControl, FormHelperText, FormLabel } from '@chakra-ui/form-control'
import { Box, Flex, Link, VStack } from '@chakra-ui/layout'
import { Switch, Text as ChakraText } from '@chakra-ui/react'
import { Select } from '@chakra-ui/select'
import type { FormikProps } from 'formik'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { GrOptimize } from 'react-icons/gr'
import { SettingsToggleBox } from '..'
import { PermissionsEnum } from '../../../../utils'
import { FeaturesEnum } from '../../../../utils/features'
import type { SettingsResponse } from '../../../interfaces'
import PermissionGuard from '../../PermissionGuard'
import { UserContext } from '../../../providers'

const ImageSettingsOptimization = ({
  values,
  setFieldValue,
  handleChange,
}: FormikProps<SettingsResponse>): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  // Check Auto AVIF from both settings and pricing plan features (same logic as admin panel)
  const isAutoAvifEnabled = values.autoAvifFormat || user?.pricingPlan?.featuresList?.includes(FeaturesEnum.AUTO_AVIF)


  return (
    <PermissionGuard
      permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
    >
      {
        (hasPermission: boolean) => (
          <AccordionItem borderTop="none">
            <AccordionButton px={12} py={8}>
              <Flex
                flex="1"
                textAlign="left"
                lineHeight="1.5em"
                fontWeight="700"
                style={{ gap: '1em' }}
              >
                <GrOptimize fontSize="1.5em" />
                {t('image_optimization', 'Optimization')}
              </Flex>
              <Box background="appPaleGreyThree" borderRadius="50%" padding="0.25em">
                <AccordionIcon color="appSlate" display="block" />
              </Box>
            </AccordionButton>

            <AccordionPanel px={12}>
              <VStack spacing="0.75em">
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t(
                        'image_optimization_best_format',
                        'Use best format for Image Delivery',
                      )}
                    </ChakraText>
                    <Switch
                      name="optimization.autoFormat"
                      isDisabled={!hasPermission}
                      isChecked={values.optimization.autoFormat === 'true'}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked.toString())
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t('image_optimization_best_format_description')}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/image-optimization/automatic-image-format-conversion"
                    >
                      {t('read_more', 'Read More')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      ON
                    </ChakraText>
                  </ChakraText>
                  <Alert status="info">
                    <AlertIcon
                      height="1em"
                      width="1em"
                      alignItems="flex-start"
                      marginTop="4px"
                    />
                    {isAutoAvifEnabled ? (
                      <AlertDescription fontSize="sm">
                        {t('avif_enabled')}
                      </AlertDescription>
                    ) : (
                      <AlertDescription fontSize="sm">
                        {t('avif_disabled')}
                      </AlertDescription>
                    )}
                  </Alert>
                </SettingsToggleBox>

                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t(
                        'image_optimization_best_quality',
                        'Optimize Image Quality before Delivery',
                      )}
                    </ChakraText>
                    <Switch
                      name="optimization.autoQuality"
                      isDisabled={!hasPermission}
                      isChecked={values.optimization.autoQuality === 'true'}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked.toString())
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t('image_optimization_best_quality_description')}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/image-optimization/quality-optimization"
                    >
                      {t('read_more', 'Read More')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      ON
                    </ChakraText>
                  </ChakraText>
                  <FormControl isDisabled={!hasPermission}>
                    <FormLabel>Default Quality</FormLabel>
                    <Select
                      name="optimization.defaultQuality"
                      maxWidth="250px"
                      value={values.optimization.defaultQuality}
                      onChange={e =>
                        void setFieldValue(
                          'optimization.defaultQuality',
                          parseInt(e.target.value, 10),
                        )
                      }
                      disabled={!hasPermission || values.optimization.autoQuality !== 'true'}
                    >
                      {[100, 95, 90, 85, 80, 75, 70, 60, 50, 40, 30, 20, 10].map(
                        x => (
                          <option key={x} value={x.toString()}>
                            {x}
                          </option>
                        ),
                      )}
                    </Select>
                  </FormControl>
                  <FormControl id="dataSaver">
                    <FormLabel>
                      {t('image_data_saver', 'Enable support for Data Saver mode')}
                    </FormLabel>
                    <ChakraText>
                      {t('image_data_saver_description')}
                      <Link
                        isExternal
                        href="https://imagekit.io/blog/lighter-automatic-responsive-images-client-hints/"
                      >
                        {t('read_more', 'Read More')}
                      </Link>
                    </ChakraText>
                    <Select
                      name="optimization.useDataSaverMode"
                      maxWidth="250px"
                      value={values.optimization.useDataSaverMode}
                      onChange={handleChange}
                      disabled={!hasPermission || values.optimization.autoQuality !== 'true'}
                    >
                      <option value="true">{t('yes', 'Yes')}</option>
                      <option value="false">{t('no', 'No')}</option>
                    </Select>
                  </FormControl>
                </SettingsToggleBox>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )
      }
    </PermissionGuard>
  )
}

export default ImageSettingsOptimization
